import React, { useEffect, useState } from 'react';
import { Mail, Phone, Linkedin, Github, MapPin, Calendar, Briefcase, BookOpen } from 'lucide-react';
import './SuperprofileResume.css';

function SuperprofileResume({ superprofile }) {
  const [parsedProfile, setParsedProfile] = useState({
    personalInfo: {
      name: '',
      location: '',
      familySituation: '',
    },
    contactInfo: {
      email: '',
      phone: '',
      linkedin: '',
      github: '',
      behance: ''
    },
    about: '',  // Add a field for the About section
    education: null, // Add a field for Education
    experience: [],
    currentStatus: {
      jobStatus: '',
      noticePeriod: '',
      salary: ''
    },
    skills: {
      core: [],
      strengths: '',
      weaknesses: ''
    },
    motivations: {
      primary: '',
      workModel: '',
      relocation: '',
      teamDynamics: '',
      company: ''
    },
    careerGoals: {
      shortTerm: '',
      dream: ''
    }
  });
  
  // Parse superprofile whenever it changes
  useEffect(() => {
    if (superprofile) {
      const profile = parseProfile(superprofile);
      setParsedProfile(profile);
    }
  }, [superprofile]);
  
  const parseProfile = (text) => {
    // Initialize the profile object with empty values
    const profile = {
      personalInfo: { name: '', location: '', familySituation: '' },
      contactInfo: { email: '', phone: '', linkedin: '', github: '', behance: '' },
      about: '',  // Initialize the about field
      education: null, // Initialize education field
      experience: [],
      currentStatus: { jobStatus: '', noticePeriod: '', salary: '' },
      skills: { core: [], strengths: '', weaknesses: '' },
      motivations: { primary: '', workModel: '', relocation: '', teamDynamics: '', company: '' },
      careerGoals: { shortTerm: '', dream: '' }
    };
    
    if (!text) return profile;
    
    // Split the text into lines
    const lines = text.split('\n');
    
    // Current section being processed
    let currentSection = '';
    let currentCompany = null;
    let collectingAbout = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) continue;
      
      // Check for section headers
      if (line.startsWith('# ')) {
        currentSection = line.substring(2);
        
        // Check if we're entering the About section
        if (currentSection === 'About') {
          collectingAbout = true;
          continue;
        } else {
          collectingAbout = false;
        }
        
        // Improve work history section detection
        if (currentSection.startsWith('Work History') || currentSection === 'Work History') {
          const companyName = currentSection.replace('Work History - ', '').replace('Work History', 'Unknown Company');
          
          // Only start a new company if this is specifically a company section
          if (currentSection.startsWith('Work History - ')) {
            currentCompany = {
              name: companyName,
              role: '',
              duration: '',
              responsibilities: []
            };
            profile.experience.push(currentCompany);
          }
        }
        
        continue;
      }
      
      // Handle About section content
      if (collectingAbout && !line.startsWith('#')) {
        profile.about += line + ' ';
        continue;
      }
      
      // Process field values
      if (line.includes(': ')) {
        const [key, value] = line.split(':', 2).map(part => part.trim());
        
        // Skip placeholder values
        if (value === '...' || !value) continue;
        
        // Map fields to the profile object
        if (currentSection === 'Personal Information') {
          if (key === 'Name') profile.personalInfo.name = value;
          else if (key === 'Location') profile.personalInfo.location = value;
          else if (key === 'Family Situation') profile.personalInfo.familySituation = value;
        } 
        else if (currentSection === 'Contact Information') {
          if (key === 'Email') profile.contactInfo.email = value;
          else if (key === 'Phone') profile.contactInfo.phone = value;
          else if (key === 'LinkedIn') profile.contactInfo.linkedin = value;
          else if (key === 'GitHub') profile.contactInfo.github = value;
          else if (key === 'Behance') profile.contactInfo.behance = value;
        }
        else if (currentSection === 'Current Job Status') {
          if (key === 'Job Looking Status') profile.currentStatus.jobStatus = value;
          else if (key === 'Notice Period') profile.currentStatus.noticePeriod = value;
          else if (key === 'Salary Expectations') profile.currentStatus.salary = value;
        }
        else if (currentSection === 'Skills & Experience') {
          if (key === 'Core Skills') {
            profile.skills.core = value.split(',').map(s => s.trim()).filter(s => s);
          }
          else if (key === 'Strengths') profile.skills.strengths = value;
          else if (key === 'Weaknesses') profile.skills.weaknesses = value;
        }
        else if (currentSection === 'Motivations & Preferences') {
          if (key === 'Primary Motivation') profile.motivations.primary = value;
          else if (key === 'Work Model Preference') profile.motivations.workModel = value;
          else if (key === 'Relocation Willingness') profile.motivations.relocation = value;
          else if (key === 'Team Dynamics') profile.motivations.teamDynamics = value;
          else if (key === 'Company Preferences') profile.motivations.company = value;
        }
        else if (currentSection === 'Career Goals') {
          if (key === 'Short-term Goal') profile.careerGoals.shortTerm = value;
          else if (key === 'Dream Job') profile.careerGoals.dream = value;
        }
        else if (currentSection === 'Education' || currentSection.startsWith('Education - ')) {
          // Handle education fields
          profile.education = profile.education || {};
          
          if (key === 'Institution') profile.education.institution = value;
          else if (key === 'Degree') profile.education.degree = value;
          else if (key === 'Field') profile.education.field = value;
          else if (key === 'Year') profile.education.year = value;
          else if (key === 'Thesis') profile.education.thesis = value;
          else if (key === 'Period') profile.education.period = value;
          else if (key === 'Grade') profile.education.grade = value;
        }
        else if (currentSection.startsWith('Work History')) {
          // Check if we're in a work history section but don't have a company object yet
          if (!currentCompany && currentSection.startsWith('Work History')) {
            currentCompany = {
              name: 'Unknown Company',
              role: '',
              duration: '',
              responsibilities: []
            };
            profile.experience.push(currentCompany);
          }
          
          // Only process if we have a company object
          if (currentCompany) {
            if (key === 'Role') currentCompany.role = value;
            else if (key === 'Duration') currentCompany.duration = value;
            else if (key === 'Company') currentCompany.name = value;
          }
        }
      }
      
      // Handle responsibility list items
      else if (line.startsWith('- ') && currentSection.startsWith('Work History') && currentCompany) {
        currentCompany.responsibilities.push(line.substring(2));
      }
    }
    
    return profile;
  };
  
  // Generate about text
  const generateAbout = () => {
    // If we have an explicit about section, use that first
    if (parsedProfile.about && parsedProfile.about.trim()) {
      return parsedProfile.about.trim();
    }
    
    // Otherwise, use the existing fallback logic
    const parts = [];
    
    // Add job status
    if (parsedProfile.currentStatus.jobStatus) {
      parts.push(`Currently ${parsedProfile.currentStatus.jobStatus.toLowerCase()}`);
    }
    
    // Add primary motivation
    if (parsedProfile.motivations.primary) {
      parts.push(parsedProfile.motivations.primary);
    }
    
    // Add work preference
    if (parsedProfile.motivations.workModel) {
      parts.push(`Seeking ${parsedProfile.motivations.workModel.toLowerCase()} opportunities`);
    }
    
    // Add family situation if present
    if (parsedProfile.personalInfo.familySituation) {
      parts.push(parsedProfile.personalInfo.familySituation);
    }
    
    if (parts.length === 0) {
      return 'Professional seeking new opportunities';
    }
    
    return parts.join('. ') + '.';
  };
  
  // Generate skills list with improved formatting
  const generateSkillsList = () => {
    let skills = [];
    
    // Handle comma-separated skills
    if (parsedProfile.skills.core && parsedProfile.skills.core.length > 0) {
      // Process each skill entry
      parsedProfile.skills.core.forEach(skill => {
        // If this looks like multiple skills squeezed together (no spaces, long string)
        if (skill.length > 15 && !/\s/.test(skill)) {
          // Try to split by capital letters (e.g., HTMLCSSJavaScript -> HTML, CSS, JavaScript)
          const splitSkills = skill.replace(/([A-Z][a-z]+)/g, ' $1').trim().split(/\s+/);
          skills.push(...splitSkills);
        } else {
          skills.push(skill);
        }
      });
    }
    
    // Add strengths if not already included
    if (parsedProfile.skills.strengths && !skills.includes(parsedProfile.skills.strengths)) {
      skills.push(parsedProfile.skills.strengths);
    }
    
    return skills;
  };
  
  return (
    <div className="resume-container">
      {/* Header */}
      <header className="resume-header">
        <h1>{parsedProfile.personalInfo.name || 'Candidate'}</h1>
        {parsedProfile.experience.length > 0 && parsedProfile.experience[0].role && (
          <h2>{parsedProfile.experience[0].role}</h2>
        )}
        
        {parsedProfile.personalInfo.location && (
          <div className="resume-location">
            <MapPin size={16} />
            <span>{parsedProfile.personalInfo.location}</span>
          </div>
        )}
        
        <div className="resume-socials">
          {parsedProfile.contactInfo.linkedin && (
            <a 
              href={parsedProfile.contactInfo.linkedin.startsWith('http') 
                ? parsedProfile.contactInfo.linkedin 
                : `https://${parsedProfile.contactInfo.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              title="LinkedIn"
            >
              <Linkedin size={20} />
            </a>
          )}
          {parsedProfile.contactInfo.github && (
            <a 
              href={parsedProfile.contactInfo.github.startsWith('http') 
                ? parsedProfile.contactInfo.github 
                : `https://${parsedProfile.contactInfo.github}`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              title="GitHub"
            >
              <Github size={20} />
            </a>
          )}
          {parsedProfile.contactInfo.behance && (
            <a 
              href={parsedProfile.contactInfo.behance.startsWith('http') 
                ? parsedProfile.contactInfo.behance 
                : `https://${parsedProfile.contactInfo.behance}`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              title="Behance"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2ZM12 20C7.589 20 4 16.411 4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 16.411 16.411 20 12 20Z" fill="currentColor"/>
                <path d="M12 6C8.686 6 6 8.686 6 12C6 15.314 8.686 18 12 18C15.314 18 18 15.314 18 12C18 8.686 15.314 6 12 6ZM12 16C9.791 16 8 14.209 8 12C8 9.791 9.791 8 12 8C14.209 8 16 9.791 16 12C16 14.209 14.209 16 12 16Z" fill="currentColor"/>
                <path d="M12 10C10.895 10 10 10.895 10 12C10 13.105 10.895 14 12 14C13.105 14 14 13.105 14 12C14 10.895 13.105 10 12 10Z" fill="currentColor"/>
              </svg>
            </a>
          )}
        </div>
      </header>

      {/* About */}
      <section className="resume-section">
        <h2>About</h2>
        <p className="resume-about">{generateAbout()}</p>
      </section>

      {/* Education */}
      {parsedProfile.education && (
        <section className="resume-section">
          <h2>Education</h2>
          <div className="education-item experience-item">
            <div className="experience-header">
              <div>
                {parsedProfile.education.institution && (
                  <h3 className="experience-title">{parsedProfile.education.institution}</h3>
                )}
                {parsedProfile.education.degree && (
                  <div className="experience-company">
                    <BookOpen size={14} className="icon" />
                    {parsedProfile.education.degree}
                    {parsedProfile.education.field && ` in ${parsedProfile.education.field}`}
                  </div>
                )}
              </div>
              {(parsedProfile.education.year || parsedProfile.education.period) && (
                <div className="experience-date">
                  <Calendar size={14} className="icon" />
                  {parsedProfile.education.year || parsedProfile.education.period}
                </div>
              )}
            </div>
            {parsedProfile.education.thesis && (
              <div className="education-thesis">
                <strong>Thesis:</strong> {parsedProfile.education.thesis}
              </div>
            )}
            {parsedProfile.education.grade && (
              <div className="education-grade">
                <strong>Grade:</strong> {parsedProfile.education.grade}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Current Status */}
      {(parsedProfile.currentStatus.jobStatus || 
        parsedProfile.currentStatus.noticePeriod || 
        parsedProfile.currentStatus.salary) && (
        <section className="resume-section">
          <h2>Current Status</h2>
          <div className="status-grid">
            {parsedProfile.currentStatus.jobStatus && (
              <div className="status-item">
                <div className="status-label">Job Status</div>
                <div className="status-value">{parsedProfile.currentStatus.jobStatus}</div>
              </div>
            )}
            {parsedProfile.currentStatus.noticePeriod && (
              <div className="status-item">
                <div className="status-label">Notice Period</div>
                <div className="status-value">{parsedProfile.currentStatus.noticePeriod}</div>
              </div>
            )}
            {parsedProfile.currentStatus.salary && (
              <div className="status-item">
                <div className="status-label">Salary Expectations</div>
                <div className="status-value">{parsedProfile.currentStatus.salary}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Skills */}
      {generateSkillsList().length > 0 && (
        <section className="resume-section">
          <h2>Skills</h2>
          <div className="resume-skills">
            {generateSkillsList().map((skill, index) => (
              <span key={index} className="skill-tag">{skill}</span>
            ))}
          </div>
        </section>
      )}

      {/* Experience */}
      {parsedProfile.experience.length > 0 && (
        <section className="resume-section">
          <h2>Experience</h2>
          {parsedProfile.experience.map((exp, index) => (
            <div key={index} className="experience-item">
              <div className="experience-header">
                <div>
                  <h3 className="experience-title">{exp.role || 'Position'}</h3>
                  <div className="experience-company">
                    <Briefcase size={14} className="icon" />
                    {exp.name}
                  </div>
                </div>
                {exp.duration && (
                  <div className="experience-date">
                    <Calendar size={14} className="icon" />
                    {exp.duration}
                  </div>
                )}
              </div>
              {exp.responsibilities.length > 0 && (
                <ul className="experience-list">
                  {exp.responsibilities.map((resp, respIndex) => (
                    <li key={respIndex}>{resp}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      )}
      
      {/* Motivations & Preferences */}
      {(parsedProfile.motivations.primary || 
        parsedProfile.motivations.workModel || 
        parsedProfile.motivations.company) && (
        <section className="resume-section">
          <h2>Motivations & Preferences</h2>
          <div className="motivations-grid">
            {parsedProfile.motivations.primary && (
              <div className="motivation-item">
                <div className="motivation-label">Primary Motivation</div>
                <div className="motivation-value">{parsedProfile.motivations.primary}</div>
              </div>
            )}
            {parsedProfile.motivations.workModel && (
              <div className="motivation-item">
                <div className="motivation-label">Work Model</div>
                <div className="motivation-value">{parsedProfile.motivations.workModel}</div>
              </div>
            )}
            {parsedProfile.motivations.relocation && (
              <div className="motivation-item">
                <div className="motivation-label">Relocation</div>
                <div className="motivation-value">{parsedProfile.motivations.relocation}</div>
              </div>
            )}
            {parsedProfile.motivations.company && (
              <div className="motivation-item">
                <div className="motivation-label">Company Preferences</div>
                <div className="motivation-value">{parsedProfile.motivations.company}</div>
              </div>
            )}
          </div>
        </section>
      )}
      
      {/* Career Goals */}
      {(parsedProfile.careerGoals.shortTerm || parsedProfile.careerGoals.dream) && (
        <section className="resume-section">
          <h2>Career Goals</h2>
          {parsedProfile.careerGoals.shortTerm && (
            <div className="goal-item">
              <div className="goal-label">Short-term Goal</div>
              <div className="goal-value">{parsedProfile.careerGoals.shortTerm}</div>
            </div>
          )}
          {parsedProfile.careerGoals.dream && (
            <div className="goal-item">
              <div className="goal-label">Dream Job</div>
              <div className="goal-value">{parsedProfile.careerGoals.dream}</div>
            </div>
          )}
        </section>
      )}

      {/* Contact */}
      <section className="resume-section">
        <h2>Contact</h2>
        <div className="resume-contact">
          {parsedProfile.contactInfo.email && (
            <div className="contact-item">
              <Mail size={16} className="icon" />
              <a href={`mailto:${parsedProfile.contactInfo.email}`}>
                {parsedProfile.contactInfo.email}
              </a>
            </div>
          )}
          
          {parsedProfile.contactInfo.phone && (
            <div className="contact-item">
              <Phone size={16} className="icon" />
              <a href={`tel:${parsedProfile.contactInfo.phone.replace(/\s+/g, '')}`}>
                {parsedProfile.contactInfo.phone}
              </a>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default SuperprofileResume;